import "./Authentication.css";
import logoLight from "../../Components/logo.png";
import logoDark from "../../Components/logo-dark.png";
import bg from "../../assets/images/logistic.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdLocalShipping } from "react-icons/md";
import {
  FaTruck,
  FaBoxOpen,
  FaWarehouse,
  FaArrowRight,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { useThemeMode } from "../../theme/ThemeModeContext";
import { useTranslation } from "react-i18next";

const AuthRoles = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resolvedTheme } = useThemeMode();
  const Logo = resolvedTheme === "dark" ? logoDark : logoLight;

  // A visitor from a referral link who goes back to this page keeps the code.
  const referralCode = searchParams.get("ref");
  const registerPath = (path) =>
    referralCode && path === "/register/shipper"
      ? `${path}?ref=${encodeURIComponent(referralCode)}`
      : path;

  // Shown in this order: Shipper, Warehouse, Carrier.
  const roleCards = [
    {
      id: "/register/shipper",
      loginId: "/login/shipper",
      title: t("roles.shipper.title"),
      subtitle: t("roles.shipper.subtitle"),
      description: t("roles.shipper.description"),
      icon: FaBoxOpen,
      gradient: "from-brand to-brand",
      shadowColor: "shadow-brand-light",
      features: t("roles.shipper.features", { returnObjects: true }),
      ideal: t("roles.shipper.ideal"),
    },
    {
      id: "/register/warehouse",
      loginId: "/login/warehouse",
      title: t("roles.warehouse.title"),
      subtitle: t("roles.warehouse.subtitle"),
      description: t("roles.warehouse.description"),
      icon: FaWarehouse,
      gradient: "from-state-success to-state-success",
      shadowColor: "shadow-emerald-100",
      features: t("roles.warehouse.features", { returnObjects: true }),
      ideal: t("roles.warehouse.ideal"),
    },
    {
      id: "/register/carrier",
      loginId: "/login/carrier",
      title: t("roles.carrier.title"),
      subtitle: t("roles.carrier.subtitle"),
      description: t("roles.carrier.description"),
      icon: FaTruck,
      gradient: "from-orange-400 to-orange-600",
      shadowColor: "shadow-state-warning-bg",
      features: t("roles.carrier.features", { returnObjects: true }),
      ideal: t("roles.carrier.ideal"),
    },
  ];

  return (
    <div
      className="loginpage__new_landing_bg register__form_screen overflow-auto"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="register__main_wrapper">
        <div className="min-h-screen flex items-center justify-center sm:p-4">
          <div className="max-w-6xl w-full">
            {/* Header Section */}
            <div className="text-center my-4">
              <div className="shipper__tab_left_logo flex justify-center">
                <img
                  src={Logo}
                  alt=""
                  className="max-w-[250px]"
                  onClick={() => {
                    window.location.href = "https://www.hajexbolt.com";
                  }}
                />
              </div>
            </div>

            {/* Role Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              {roleCards.map((role) => {
                const IconComponent = role.icon;
                return (
                  <div
                    key={role.id}
                    className={`bg-ui-surface rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${role.shadowColor} shadow-md border border-ui-surface-sunken group`}
                  >
                    {/* Card Header */}
                    <div className="text-center mb-4">
                      <div
                        className={`w-12 h-12 bg-gradient-to-r ${role.gradient} rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-105 transition-transform duration-300`}
                      >
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-ui-text mb-1">
                        {role.title}
                      </h3>
                      <p className="text-xs font-medium text-ui-text-muted uppercase tracking-wide">
                        {role.subtitle}
                      </p>
                    </div>

                    {/* Description */}
                    <p className="text-ui-text-muted text-center mb-4 leading-relaxed text-sm">
                      {role.description}
                    </p>

                    {/* Features List */}
                    <div className="space-y-2 mb-4">
                      {role.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-2 text-xs"
                        >
                          <div
                            className={`w-1.5 h-1.5 bg-gradient-to-r ${role.gradient} rounded-full mt-1.5 flex-shrink-0`}
                          ></div>
                          <span className="text-ui-text">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Call to Action Buttons */}
                    <div className="flex xl:space-x-3 flex-wrap justify-between">
                      <button
                        className={`flex-1 my-1 min-w-[140px] bg-gradient-to-r ${role.gradient} text-white py-2 px-4 rounded-lg text-sm sm:text-base font-medium flex items-center justify-center space-x-2 hover:shadow-md transition-all duration-300`}
                        onClick={() => navigate(registerPath(role.id))}
                      >
                        <span>{t("roles.register")}</span>
                        <FaArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
                      </button>
                      <button
                        className={`flex-1 my-1 min-w-[140px] bg-ui-surface text-ui-text py-2 px-4 rounded-lg text-sm sm:text-base font-medium flex items-center justify-center space-x-2 border border-ui-border hover:bg-ui-surface-sunken hover:shadow-md transition-all duration-300`}
                        onClick={() => navigate(role.loginId)}
                      >
                        <span>{t("roles.login")}</span>
                        <FaArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 mb-2 text-center">
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 text-ui-text-subtle">
                <div className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="w-4 h-4" />
                  <span className="text-sm">{t("roles.trust.zones")}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaBoxOpen className="w-4 h-4" />
                  <span className="text-sm">
                    {t("roles.trust.multiCarrier")}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <MdLocalShipping className="w-4 h-4" />
                  <span className="text-sm">{t("roles.trust.tracking")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthRoles;
